import React from 'react';
import { Loader2, X } from 'lucide-react';

// --- Button ---
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg' | 'icon';
  isLoading?: boolean;
}

export const Button: React.FC<ButtonProps> = ({ 
  children, 
  variant = 'primary', 
  size = 'md', 
  className = '', 
  isLoading, 
  disabled, 
  ...props 
}) => {
  const baseStyles = "inline-flex items-center justify-center rounded-full font-semibold tracking-[-0.005em] transition-[background-color,color,filter,transform,box-shadow] duration-200 active:scale-[0.985] focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-500/25 disabled:opacity-40 disabled:pointer-events-none";
  
  const variants = {
    primary: "bg-accent text-white hover:brightness-110 border border-transparent",
    secondary: "bg-card text-ink hover:bg-card-hi border border-transparent",
    outline: "border border-line bg-transparent text-ink hover:bg-card",
    ghost: "bg-transparent text-ink-2 hover:bg-card hover:text-ink",
    danger: "bg-[#ff3b30] text-white hover:brightness-110 dark:bg-[#ff453a]"
  };

  const sizes = {
    sm: "h-8 gap-1.5 px-3.5 text-[13px]",
    md: "h-10 gap-2 px-5 text-[14.5px]",
    lg: "h-12 gap-2 px-7 text-[16px]",
    icon: "h-9 w-9 p-0"
  };

  return (
    <button 
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
      {children}
    </button>
  );
};

// --- Card ---
type CardProps = React.HTMLAttributes<HTMLDivElement> & { children: React.ReactNode };

export const Card: React.FC<CardProps> = ({ children, className = '', ...props }) => (
  <div
    {...props}
    className={`bg-raised rounded-[18px] ring-1 ring-black/[0.05] shadow-[0_1px_2px_rgba(0,0,0,0.03)] dark:ring-white/[0.07] dark:shadow-none ${className}`}
  >
    {children}
  </div>
);

// --- Badge ---
export const Badge: React.FC<{ children: React.ReactNode; variant?: 'success' | 'warning' | 'info' | 'default' | 'primary' | 'neutral'; className?: string; title?: string }> = ({ children, variant = 'default', className = '', title }) => {
  const styles = {
    success: "bg-[#34c759]/[0.14] text-[#248a3d] dark:text-[#30d158]",
    warning: "bg-[#ff9f0a]/[0.15] text-[#b25000] dark:text-[#ffb340]",
    info: "bg-accent/[0.12] text-accent",
    default: "bg-card text-ink-2",
    primary: "bg-accent/[0.12] text-accent",
    neutral: "bg-card text-ink-2",
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[12px] font-semibold ${styles[variant]} ${className}`} title={title}>
      {children}
    </span>
  );
};

// --- Modal ---
import { createPortal } from 'react-dom';
import { useTransition } from '../hooks/useTransition';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}

export const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, children, footer }) => {
  const { shouldMount, isVisible } = useTransition(isOpen, 200);
  
  if (!shouldMount) return null;

  return createPortal(
    <div 
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/25 dark:bg-black/60 backdrop-blur-[6px] ${isVisible ? 'modal-backdrop-in' : 'modal-backdrop-out'}`}
      onClick={onClose}
    >
      <div 
        className={`bg-raised rounded-[22px] shadow-window max-w-lg w-full max-h-[90vh] flex flex-col overflow-hidden ${isVisible ? 'modal-content-in' : 'modal-content-out'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 pt-5 pb-1">
          <h3 className="text-[17px] font-semibold tracking-[-0.01em] text-ink">{title}</h3>
          <button onClick={onClose} aria-label="Close" className="grid h-7 w-7 place-items-center rounded-full bg-card text-ink-2 hover:bg-card-hi hover:text-ink transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="px-6 py-4 overflow-y-auto">
          {children}
        </div>
        {footer && (
          <div className="px-6 pb-5 pt-2 flex justify-end gap-2.5">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

// --- Confirm Dialog ---
interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'primary';
  isLoading?: boolean;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'primary',
  isLoading = false,
}) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            {cancelText}
          </Button>
          <Button 
            variant={variant}
            onClick={onConfirm}
            isLoading={isLoading}
          >
            {confirmText}
          </Button>
        </>
      }
    >
      <div className="text-[15px] leading-relaxed text-ink-2">
        {message}
      </div>
    </Modal>
  );
};

// --- Progress Bar ---
export const ProgressBar: React.FC<{ progress: number; label?: string }> = ({ progress, label }) => (
  <div className="w-full">
    {label && (
      <div className="flex justify-between mb-1 text-sm font-medium text-slate-700 dark:text-zinc-300">
        <span>{label}</span>
        <span>{Math.round(progress)}%</span>
      </div>
    )}
    <div className="w-full bg-card-hi rounded-full h-1.5 overflow-hidden">
      <div 
        className="bg-accent h-1.5 rounded-full transition-all duration-300 ease-in-out" 
        style={{ width: `${progress}%` }}
      ></div>
    </div>
  </div>
);
